import {
  patchState,
  signalStore,
  withMethods,
  withState,
} from '@ngrx/signals';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { concatMap, filter, pipe, switchMap, tap } from 'rxjs';
import { HttpErrorResponse, HttpEventType, HttpResponse, HttpEvent } from '@angular/common/http';
import { tapResponse } from '@ngrx/operators';
import {
  setPropLoaded,
  setPropLoading,
  withReqState,
  setPropError,
  setPropInit,
} from '@application/data-access';
import { StorageService } from './infra/storage.service';
import { eStorageFolder } from './entities/storage.enum';
import { ErrorStore } from '@application/errors/data-access';
import { StorageFileUploadResponse } from './entities/storage.model';

export interface IStorageStore {
  ObjectNameById: Record<string, string>;
  ObjectUrl: Record<string, any>;
  ObjectUrlState: Record<string, 'loading' | 'loaded' | 'error'>;
  UploadedFile: StorageFileUploadResponse | null;
  UploadedFileByKey: Record<string, StorageFileUploadResponse | null>;
  FileState: Record<string, 'init' | 'loading' | 'loaded' | 'error'>;
  LastUploadFolder: eStorageFolder | null;
  uploadFileProgress: number;
}

export const storageInitialState: IStorageStore = {
  ObjectNameById: {},
  ObjectUrl: {},
  ObjectUrlState: {},
  UploadedFile: null,
  UploadedFileByKey: {},
  FileState: {},
  LastUploadFolder: null,
  uploadFileProgress: 0,
};

export const StorageStore = signalStore(
  { providedIn: 'root' },
  withState<IStorageStore>(storageInitialState),
  withMethods(
    (
      state,
      storageService = inject(StorageService),
      errorStore = inject(ErrorStore)
    ) => {
    const base64toFile = (base64: string, filename: string, folder?: eStorageFolder) => {
        // Avatar uploads (UserPhotos) must always be PNG
        if (folder === eStorageFolder.UserPhotos) {
          const base64Data = base64.replace(/^data:[^;]+;base64,/, '');
          const byteString = atob(base64Data);
          const arrayBuffer = new ArrayBuffer(byteString.length);
          const int8Array = new Uint8Array(arrayBuffer);

          for (let i = 0; i < byteString.length; i++) {
            int8Array[i] = byteString.charCodeAt(i);
          }

          const imageBlob = new Blob([int8Array], { type: 'image/png' });
          const file = new File([imageBlob], filename, { type: 'image/png' });

          return file;
        }

        // Improved regex to capture MIME type from data URI, including jpeg/jpg variations
        const match = base64.match(/^data:([^;]+);base64,/);
        let mimeType = 'image/jpeg';
        let base64Data = base64;

        if (match && match[1]) {
          mimeType = match[1];
          base64Data = base64.replace(/^data:[^;]+;base64,/, '');
        } else {
          // Fallback to extension detection if no data URI prefix
          const extension = filename?.split('.')?.pop()?.toLowerCase();
          switch (extension) {
            case 'png':
              mimeType = 'image/png';
              break;
            case 'jpg':
            case 'jpeg':
              mimeType = 'image/jpeg';
              break;
            case 'gif':
              mimeType = 'image/gif';
              break;
            case 'webp':
              mimeType = 'image/webp';
              break;
            case 'pdf':
              mimeType = 'application/pdf';
              break;
            case 'mp4':
              mimeType = 'video/mp4';
              break;
            case 'webm':
              mimeType = 'video/webm';
              break;
            case 'ogg':
              mimeType = 'video/ogg';
              break;
            default:
              mimeType = 'image/jpeg'; // Default to jpeg instead of octet-stream for images
          }
        }

        const byteString = atob(base64Data);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const int8Array = new Uint8Array(arrayBuffer);

        for (let i = 0; i < byteString.length; i++) {
          int8Array[i] = byteString.charCodeAt(i);
        }

        const imageBlob = new Blob([int8Array], { type: mimeType });
        const file = new File([imageBlob], filename, { type: mimeType });

        return file;
      };
      return {
        getObjectNameById: rxMethod<string>(
          pipe(
            filter((id) => !!id && state.ObjectNameById()[id] === undefined),
            tap((id) => patchState(state, { ObjectNameById: { ...state.ObjectNameById(), [id]: null }, ...setPropLoading('getObjectNameById') })),
            concatMap((id) =>
              storageService.getObjectNameById(id).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(state, { ObjectNameById: { ...state.ObjectNameById(), [id]: response || null }, ...setPropLoaded('getObjectNameById') });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(state, { ObjectNameById: { ...state.ObjectNameById() }, ...setPropError('getObjectNameById', error) });
                  },
                })
              ),
            )
          )
        ),
        getObjectUrl: rxMethod<string>(
          pipe(
            filter((id) => !!id && state.ObjectUrl()[id] === undefined),
            tap((id ) => patchState(state, { ObjectUrl: { ...state.ObjectUrl(), [id]: null }, ObjectUrlState: { ...state.ObjectUrlState(), [id]: 'loading' } } )),
            concatMap((id) =>
              storageService.getObjectUrl(id).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(state, { ObjectUrl: { ...state.ObjectUrl(), [id]: response[id] ?? null }, ObjectUrlState: { ...state.ObjectUrlState(), [id]: 'loaded' } });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(state, { ObjectUrl: { ...state.ObjectUrl()}, ObjectUrlState: { ...state.ObjectUrlState(), [id]: 'error' } });
                  },
                })
              ),
            )
          )
        ),
      uploadFile: rxMethod<{ base64: string, folder: eStorageFolder, filename?: string, fileStateKey?: string }>(
          pipe(
            tap(({ fileStateKey }) => patchState(state, {  ...setPropLoading('uploadFile'), uploadFileProgress: 0, FileState: { ...state.FileState(), [fileStateKey]: 'loading' } })),
            // `concatMap`: o store é `providedIn: 'root'`, logo este `rxMethod` é
            // uma subscrição única partilhada por todos os consumidores. Com
            // `switchMap`, um segundo upload — do mesmo ecrã em lote ou de outro
            // componente aberto em simultâneo — abortava o anterior sem erro, e o
            // ficheiro perdia-se em silêncio. Enfileirar serializa os uploads e
            // mantém `UploadedFile`/`uploadFileProgress` coerentes com o que está
            // de facto a subir.
            concatMap(({ base64, folder, filename, fileStateKey }) =>
              storageService.uploadFile(base64toFile(base64, filename, folder), folder).pipe(
                tapResponse({
                  next: (response: HttpEvent<StorageFileUploadResponse>) => {
                    if (response.type === HttpEventType.UploadProgress) {
                      const progress = Math.round(100 * (response.loaded / (response.total ?? 1)));
                      patchState(state, { uploadFileProgress: progress });
                      return;
                    }
                    if (response instanceof HttpResponse) {
                      const UploadedFile = response.body;
                      patchState(state, {
                        UploadedFile,
                        UploadedFileByKey: { ...state.UploadedFileByKey(), [fileStateKey]: UploadedFile },
                        LastUploadFolder: folder,
                        ...setPropLoaded('uploadFile'),
                        FileState: { ...state.FileState(), [fileStateKey]: 'loaded' }
                      });
                    }
                  },
                  error: (error: HttpErrorResponse) => {
                    console.error(error);
                    errorStore.setHttpError(error);
                    patchState(state, { ...setPropError('uploadFile', error), FileState: { ...state.FileState(), [fileStateKey]: 'error' } });
                  },
                })
              ),
            )
          )
        ),
        setUploadFileBlankState: () => {
          patchState(state, { UploadedFile: null, LastUploadFolder: null, ...setPropInit('uploadFile', 'uploadFileAnonymous') });
        },
        clearUploadedFileByKey: (fileStateKey: string) => {
          const updatedFiles = { ...state.UploadedFileByKey() };
          delete updatedFiles[fileStateKey];
          patchState(state, { 
            UploadedFileByKey: updatedFiles, 
            FileState: { ...state.FileState(), [fileStateKey]: 'init' } 
          });
        },
        blankPropState: (fileStateKey: string) => {
          patchState(state, { FileState: { ...state.FileState(), [fileStateKey]: 'init' } });
        },
        clearObjectUrlCache: (id?: string) => {
          if (id) {
            const updatedUrls = { ...state.ObjectUrl() };
            const updatedStates = { ...state.ObjectUrlState() };
            delete updatedUrls[id];
            delete updatedStates[id];
            patchState(state, { ObjectUrl: updatedUrls, ObjectUrlState: updatedStates });
          } else {
            patchState(state, { ObjectUrl: {}, ObjectUrlState: {} });
          }
        },
        uploadFileAnonymous: rxMethod<{ base64: string, folder: eStorageFolder, filename?: string, fileStateKey?: string, tenantId: string }>(
          pipe(
            tap(({ fileStateKey }) => patchState(state, {  ...setPropLoading('uploadFileAnonymous'), FileState: { ...state.FileState(), [fileStateKey]: 'loading' } })),
            // Enfileira em vez de cancelar, pelo mesmo motivo do `uploadFile`.
            concatMap(({ base64, folder, filename, fileStateKey, tenantId }) =>
              storageService.uploadFileAnonymous(base64toFile(base64, filename, folder), folder, tenantId).pipe(
                tapResponse({
                  next: (UploadedFile) => {
                    patchState(state, { 
                      UploadedFile, 
                      UploadedFileByKey: { ...state.UploadedFileByKey(), [fileStateKey]: UploadedFile },
                      ...setPropLoaded('uploadFileAnonymous'), 
                      FileState: { ...state.FileState(), [fileStateKey]: 'loaded' } 
                    });
                  },
                  error: (error: HttpErrorResponse) => {
                    console.error(error);
                    errorStore.setHttpError(error);
                    patchState(state, { ...setPropError('uploadFileAnonymous', error), FileState: { ...state.FileState(), [fileStateKey]: 'error' } });
                  },
                })
              ),
            )
          )
        ),
      };
    }
  ),
  withReqState('getObjectNameById', 'uploadFile', 'uploadFileAnonymous')
);
